import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "影视分类", to: "/category" },
  { label: "影视资讯", to: "/news" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setKeyword(location.pathname === "/search" ? params.get("q") || "" : "");
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200/80" : "bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-content items-center gap-4 px-4 md:h-[72px] md:px-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-film-line text-lg leading-none" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-bold tracking-tight text-foreground-950 md:text-lg">
              女友的妈妈
            </span>
            <span className="mt-1 text-[10px] font-medium tracking-[0.18em] text-foreground-500">FILM PORTAL</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-primary-50 font-medium text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden w-full max-w-[320px] items-center md:flex">
          <div className="relative w-full">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base leading-none" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名称、资讯标题"
              aria-label="站内搜索"
              className="h-10 w-full rounded-md border border-background-300/80 bg-background-100 pl-9 pr-[76px] text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-100"
            />
            <button
              type="submit"
              className="absolute right-1 top-1 h-8 cursor-pointer whitespace-nowrap rounded bg-primary-500 px-3 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/80 text-foreground-800 md:hidden"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl leading-none`} />
        </button>
      </div>

      <div
        ref={menuRef}
        className={`overflow-hidden border-t border-background-200/80 bg-background-50 transition-all duration-300 md:hidden ${
          menuOpen ? "max-h-[420px]" : "max-h-0 border-t-0"
        }`}
      >
        <div className="space-y-4 px-4 py-4">
          <form onSubmit={submitSearch} className="relative w-full">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base leading-none" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名称、资讯标题"
              aria-label="站内搜索"
              className="h-11 w-full rounded-md border border-background-300/80 bg-background-100 pl-9 pr-20 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
            />
            <button
              type="submit"
              className="absolute right-1 top-1 h-9 cursor-pointer whitespace-nowrap rounded bg-primary-500 px-4 text-xs font-medium text-background-50"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                    isActive ? "bg-primary-50 font-medium text-primary-700" : "text-foreground-800"
                  }`
                }
              >
                {item.label}
                <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                  <i className="ri-arrow-right-s-line text-lg leading-none" />
                </span>
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
}